/**
 * AssignDriverModal - Modal para atribuir motorista a uma rota
 *
 * Funcionalidades:
 * - Selecionar motorista de uma lista
 * - Ver disponibilidade do motorista
 * - Enviar notificacao opcional
 * - Transferir rota entre motoristas
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  Divider,
  TextField,
  InputAdornment,
  Chip,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  DirectionsCar as VehicleIcon,
  CheckCircle as ActiveIcon,
  SwapHoriz as TransferIcon,
  NotificationsActive as NotifyIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useEffectAsync } from '../../reactHelper';
import driverAssignmentService from '../services/driverAssignmentService';

const AssignDriverModal = ({
  open,
  onClose,
  onAssign,
  planId,
  planVehicleId,
  currentDriverId = null,
  vehicleName = '',
  mode = 'assign', // 'assign' | 'transfer'
}) => {
  const tokens = usePremiumTokens();

  // Estado
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [sendNotification, setSendNotification] = useState(true);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Carregar motoristas
  useEffectAsync(async () => {
    if (!open) return;

    setIsLoading(true);
    setError(null);

    try {
      const driversData = await driverAssignmentService.getDriversWithAssignments();
      setDrivers(driversData);
      setFilteredDrivers(driversData);
    } catch (err) {
      console.error('[AssignDriverModal] Error loading drivers:', err);
      setError('Erro ao carregar motoristas');
    } finally {
      setIsLoading(false);
    }
  }, [open]);

  // Filtrar motoristas por busca
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredDrivers(drivers);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = drivers.filter((driver) => {
      const name = (driver.name || '').toLowerCase();
      const uniqueId = (driver.uniqueId || '').toLowerCase();
      const phone = (driver.attributes?.phone || '').toLowerCase();
      return name.includes(query) || uniqueId.includes(query) || phone.includes(query);
    });

    setFilteredDrivers(filtered);
  }, [searchQuery, drivers]);

  // Reset ao fechar
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSelectedDriver(null);
      setSendNotification(true);
      setNotificationMessage('');
      setTransferReason('');
      setError(null);
    }
  }, [open]);

  // Handler de selecao
  const handleSelectDriver = useCallback((driver) => {
    setSelectedDriver((prev) => (prev?.id === driver.id ? null : driver));
  }, []);

  // Handler de atribuicao
  const handleAssign = useCallback(async () => {
    if (!selectedDriver) return;

    setIsSaving(true);
    setError(null);

    try {
      if (mode === 'transfer') {
        await driverAssignmentService.transferRoute(
          planId,
          planVehicleId,
          selectedDriver.id,
          transferReason
        );
      } else {
        await driverAssignmentService.assignDriverToRoute(
          planId,
          planVehicleId,
          selectedDriver.id,
          {
            notify: sendNotification,
            notificationMessage: notificationMessage || undefined,
          }
        );
      }

      if (onAssign) {
        onAssign({
          driverId: selectedDriver.id,
          driverName: selectedDriver.name,
          mode,
        });
      }

      onClose();
    } catch (err) {
      console.error('[AssignDriverModal] Error assigning driver:', err);
      setError(`Erro ao ${mode === 'transfer' ? 'transferir' : 'atribuir'}: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  }, [selectedDriver, mode, planId, planVehicleId, sendNotification, notificationMessage, transferReason, onAssign, onClose]);

  // Obter status badge do motorista
  const getDriverStatusBadge = (driver) => {
    if (driver.id === currentDriverId) {
      return (
        <Chip
          label="Atual"
          size="small"
          sx={{
            backgroundColor: `${tokens?.accent?.primary}20`,
            color: tokens?.accent?.primary,
            fontWeight: 600,
            fontSize: '10px',
          }}
        />
      );
    }

    if (driver.hasActiveAssignment) {
      return (
        <Chip
          label="Em Rota"
          size="small"
          icon={<VehicleIcon sx={{ fontSize: '14px !important' }} />}
          sx={{
            backgroundColor: `${tokens?.semantic?.warning}20`,
            color: tokens?.semantic?.warning,
            fontWeight: 600,
            fontSize: '10px',
          }}
        />
      );
    }

    return (
      <Chip
        label="Disponivel"
        size="small"
        icon={<ActiveIcon sx={{ fontSize: '14px !important' }} />}
        sx={{
          backgroundColor: `${tokens?.semantic?.success}20`,
          color: tokens?.semantic?.success,
          fontWeight: 600,
          fontSize: '10px',
        }}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: tokens?.surface?.raised,
          backgroundImage: 'none',
          borderRadius: tokens?.radius?.lg,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${tokens?.border?.subtle}`,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {mode === 'transfer' ? (
            <TransferIcon sx={{ color: tokens?.accent?.primary }} />
          ) : (
            <PersonIcon sx={{ color: tokens?.accent?.primary }} />
          )}
          <Typography
            sx={{
              color: tokens?.text?.primary,
              fontWeight: 600,
              fontSize: '18px',
            }}
          >
            {mode === 'transfer' ? 'Transferir Rota' : 'Atribuir Motorista'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: tokens?.text?.tertiary }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Info do veiculo */}
        {vehicleName && (
          <Box
            sx={{
              px: 3,
              py: 2,
              backgroundColor: tokens?.surface?.base,
              borderBottom: `1px solid ${tokens?.border?.subtle}`,
            }}
          >
            <Typography sx={{ fontSize: '13px', color: tokens?.text?.tertiary }}>
              Veiculo
            </Typography>
            <Typography sx={{ fontSize: '15px', color: tokens?.text?.primary, fontWeight: 500 }}>
              {vehicleName}
            </Typography>
          </Box>
        )}

        {/* Erro */}
        {error && (
          <Alert severity="error" sx={{ mx: 3, mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Campo de busca */}
        <Box sx={{ px: 3, pt: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar motorista..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: tokens?.text?.tertiary, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: tokens?.surface?.base,
              },
            }}
          />
        </Box>

        {/* Lista de motoristas */}
        <Box sx={{ px: 3, py: 2, maxHeight: 300, overflow: 'auto' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : filteredDrivers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PersonIcon sx={{ fontSize: 48, color: tokens?.text?.tertiary, mb: 1 }} />
              <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '14px' }}>
                {searchQuery ? 'Nenhum motorista encontrado' : 'Nenhum motorista disponivel'}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filteredDrivers.map((driver, index) => {
                const isSelected = selectedDriver?.id === driver.id;
                const isCurrent = driver.id === currentDriverId;

                return (
                  <Box key={driver.id}>
                    {index > 0 && <Divider sx={{ borderColor: tokens?.border?.subtle }} />}
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => !isCurrent && handleSelectDriver(driver)}
                      disabled={isCurrent && mode !== 'transfer'}
                      sx={{
                        borderRadius: tokens?.radius?.md,
                        mb: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: `${tokens?.accent?.primary}15`,
                          '&:hover': {
                            backgroundColor: `${tokens?.accent?.primary}20`,
                          },
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: isSelected
                              ? tokens?.accent?.primary
                              : tokens?.surface?.elevated,
                            color: isSelected ? '#fff' : tokens?.text?.secondary,
                          }}
                        >
                          {driver.name?.[0]?.toUpperCase() || <PersonIcon />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            sx={{
                              color: tokens?.text?.primary,
                              fontWeight: isSelected ? 600 : 400,
                              fontSize: '14px',
                            }}
                          >
                            {driver.name || 'Sem nome'}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            sx={{
                              color: tokens?.text?.tertiary,
                              fontSize: '12px',
                            }}
                          >
                            {driver.uniqueId || driver.attributes?.phone || '-'}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        {getDriverStatusBadge(driver)}
                      </ListItemSecondaryAction>
                    </ListItemButton>
                  </Box>
                );
              })}
            </List>
          )}
        </Box>

        <Divider sx={{ borderColor: tokens?.border?.subtle }} />

        {/* Opcoes adicionais */}
        <Box sx={{ px: 3, py: 2 }}>
          {/* Motivo da transferencia (apenas no modo transfer) */}
          {mode === 'transfer' && (
            <TextField
              fullWidth
              size="small"
              label="Motivo da transferencia"
              value={transferReason}
              onChange={(e) => setTransferReason(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
          )}

          {/* Notificacao */}
          <FormControlLabel
            control={
              <Checkbox
                checked={sendNotification}
                onChange={(e) => setSendNotification(e.target.checked)}
                sx={{
                  color: tokens?.text?.tertiary,
                  '&.Mui-checked': {
                    color: tokens?.accent?.primary,
                  },
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <NotifyIcon sx={{ fontSize: 16, color: tokens?.text?.tertiary }} />
                <Typography sx={{ fontSize: '13px', color: tokens?.text?.secondary }}>
                  Notificar motorista
                </Typography>
              </Box>
            }
          />

          {/* Mensagem personalizada */}
          {sendNotification && (
            <TextField
              fullWidth
              size="small"
              label="Mensagem personalizada (opcional)"
              value={notificationMessage}
              onChange={(e) => setNotificationMessage(e.target.value)}
              placeholder="Deixe em branco para usar a mensagem padrao"
              sx={{ mt: 1 }}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${tokens?.border?.subtle}`,
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: tokens?.text?.secondary,
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={!selectedDriver || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : (mode === 'transfer' ? <TransferIcon /> : <PersonIcon />)}
          sx={{
            backgroundColor: tokens?.accent?.primary,
            '&:hover': {
              backgroundColor: tokens?.accent?.hover,
            },
            '&.Mui-disabled': {
              backgroundColor: tokens?.surface?.elevated,
            },
          }}
        >
          {isSaving
            ? 'Salvando...'
            : mode === 'transfer'
              ? 'Transferir'
              : 'Atribuir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignDriverModal;
